import { FormFeedBack, ModalBase, generateAvatarInitialsImage } from '@repo/ui';
import { PartnersServiceInterface } from 'domain/services';
import { useFormik } from 'formik';
import { useRef, useState } from 'react';
import { Col, Form, Input, Label, Row } from 'reactstrap';
import * as Yup from 'yup';

const ALLOWED_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];
const MAX_SIZE = 5 * 1024 * 1024;

interface IProps {
  isOpen: boolean;
  partnersService: PartnersServiceInterface;
  toggleModal: () => void;
  onCreated: (partner: { id: number; name: string; logoKey?: string }) => void;
  onKeyGenerated: (key: string, partnerName: string) => void;
}

export const ModalCreatePartnerBase = ({
  isOpen,
  partnersService,
  toggleModal,
  onCreated,
  onKeyGenerated,
}: IProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { name: '', logo: '' },
    validationSchema: Yup.object({
      name: Yup.string().required('Campo obrigatório'),
    }),
    onSubmit: async (values, helpers) => {
      setLoading(true);
      try {
        const partner = await partnersService.createPartner({
          name: values.name,
          logo: values.logo || undefined,
        });
        onCreated(partner);
        onKeyGenerated(partner.apiKey, partner.name);
        helpers.resetForm();
        toggleModal();
      } finally {
        setLoading(false);
      }
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type) || file.size > MAX_SIZE) {
      alert('Apenas arquivos .png, .jpg e .jpeg são aceitos e devem ser menores que 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      formik.setFieldValue('logo', reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const avatarSrc = formik.values.logo || (formik.values.name ? generateAvatarInitialsImage(formik.values.name) : generateAvatarInitialsImage('?'));

  return (
    <ModalBase.Wrapper
      size="sm"
      isOpen={isOpen}
      loading={loading}
      toggle={toggleModal}
      onClosed={formik.resetForm}
    >
      <ModalBase.Header toggle={toggleModal} withBorderBottom>
        Novo Parceiro
      </ModalBase.Header>

      <Form autoComplete="off" onSubmit={formik.handleSubmit}>
        <ModalBase.Body>
          <Row>
            <Col md={12} className="mb-3 d-flex justify-content-center">
              <div
                style={{ position: 'relative', cursor: 'pointer', width: 80, height: 80 }}
                onClick={() => fileInputRef.current?.click()}
              >
                <img
                  src={avatarSrc}
                  alt="Logo"
                  className="rounded-circle object-fit-cover"
                  style={{ width: 80, height: 80 }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className="ri-camera-2-fill text-white" style={{ fontSize: 13 }} />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpg,image/jpeg"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>
            </Col>

            <Col md={12}>
              <Label htmlFor="partnerBaseName" className="form-label">
                Nome do parceiro
              </Label>
              <Input
                id="partnerBaseName"
                type="text"
                name="name"
                autoFocus
                className="form-control"
                placeholder="Ex: PREMIA"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.name && !!formik.errors.name}
              />
              <FormFeedBack
                error={formik.errors.name}
                isInvalid={formik.touched.name && !!formik.errors.name}
              />
            </Col>
          </Row>
        </ModalBase.Body>

        <ModalBase.Footer>
          <ModalBase.Actions
            loading={loading}
            goBack={toggleModal}
            actionBtnType="submit"
            confirmBtnLabel="Criar parceiro"
          />
        </ModalBase.Footer>
      </Form>
    </ModalBase.Wrapper>
  );
};
